import { UserModule } from './api/methodsApi';

export type ModuleGroup = {
  years: number[];
  modules: UserModule[];
};

const compareModuleGroupsByYears = (a: ModuleGroup, b: ModuleGroup) => {
  const minYearA = Math.min(...a.years);
  const minYearB = Math.min(...b.years);

  if (minYearA !== minYearB) {
    return minYearA - minYearB;
  }

  return a.years.length - b.years.length;
};

export const buildSubtitle = (
  yearTranslation: { year: string; primary: string; standard: string },
  years: number[],
) => {
  const { year, primary, standard } = yearTranslation;

  const formattedYears = years.map(x => `${x}<sup>${x === 1 ? primary : standard}</sup>`);

  const caption =
    formattedYears.length > 2
      ? `${formattedYears.slice(0, -1).join(', ')} & ${formattedYears.slice(-1)}`
      : formattedYears.join(' & ');

  return <span dangerouslySetInnerHTML={{ __html: `${caption} ${year}` }} />;
};

export const sortModules = (modules: UserModule[], isTeacherAlike: boolean): ModuleGroup[] => {
  const groups = new Map<string, UserModule[]>();

  // group by years
  modules.forEach(module => {
    const yearKey = module.years
      .map(Number)
      .sort((a, b) => a - b)
      .join(',');
    if (!groups.has(yearKey)) {
      groups.set(yearKey, []);
    }
    groups.get(yearKey)!.push(module);
  });

  // sort by years
  let sortedAndGroupedByYears = Array.from(groups.entries())
    .map(([key, modules]) => ({
      years: key.split(',').map(Number),
      modules,
    }))
    .sort(compareModuleGroupsByYears);

  if (!isTeacherAlike) {
    sortedAndGroupedByYears.reverse();
  }

  // sort by subjectSlug and slug
  const sortedBySubjectSlugAndSlug = sortedAndGroupedByYears.map(group => {
    const subjectGroups = new Map<string, UserModule[]>();

    group.modules.forEach(module => {
      if (!subjectGroups.has(module.subjectSlug)) {
        subjectGroups.set(module.subjectSlug, []);
      }
      subjectGroups.get(module.subjectSlug)!.push(module);
    });

    const sortedModules = Array.from(subjectGroups.entries())
      .sort(([subjectA], [subjectB]) => subjectA.localeCompare(subjectB))
      .flatMap(([_, modules]) => modules.sort((a, b) => a.slug.localeCompare(b.slug)));

    return {
      years: group.years,
      modules: sortedModules,
    };
  });
  return sortedBySubjectSlugAndSlug;
};

export const mergePhasedOutModulesIntoGroups = (
  groupedModules: ModuleGroup[],
  phasedOutModules: ModuleGroup[],
  isTeacherAlike: boolean,
): ModuleGroup[] => {
  const groupsByYears = new Map<string, ModuleGroup>();

  groupedModules.forEach(group => {
    const yearsKey = group.years.join(',');

    groupsByYears.set(yearsKey, {
      years: group.years,
      modules: [...group.modules],
    });
  });

  phasedOutModules.forEach(group => {
    const yearsKey = group.years.join(',');
    const existingGroup = groupsByYears.get(yearsKey);

    if (existingGroup) {
      existingGroup.modules.push(...group.modules);
      return;
    }

    groupsByYears.set(yearsKey, {
      years: group.years,
      modules: [...group.modules],
    });
  });

  const mergedGroups = Array.from(groupsByYears.values()).sort(compareModuleGroupsByYears);

  if (!isTeacherAlike) {
    mergedGroups.reverse();
  }

  return mergedGroups;
};
